import moment from 'moment';
import { customFetchV2 } from './custom-fetch-v2';
import LocationBlock from './LocationBlock';

class LocationDataService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Retrieves blocks of location data for devices.
   *
   * For each deviceId requested, a LocationBlock is returned that contains
   * all known position data for the device within the requested time range.
   *
   * Since device location data is fragmented over different sources (usually
   * the most recent 7 days are in the database and older data is in S3) this
   * routine does the work of collating the various segments of data from
   * different sources and merges them into a single block of location data
   * per device.
   *
  */
  loadLocationsBetween(deviceIds, startAt, endAt) {
    const sig = `loadLocationsBetween(${deviceIds}, ${startAt}, ${endAt})`;
    this.info(`${sig} - start`);

    const startAtParam = moment(startAt).toISOString();
    const endAtParam = moment(endAt).toISOString();
    const url = encodeURI(`${this.baseUrl}/api/location-data.json?`
      + `start=${startAtParam}&`
      + `end=${endAtParam}&`
      + `devices=${deviceIds.join(',')}`);

    return customFetchV2(url, {
      method: 'GET',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`${sig} - Failed to retrieve location data - HTTP response ${response.status}`);
        }

        // The JSON received includes deviceKey attribs + location `segments`
        // which is an array of items where each item includes some block of
        // location data with start/end times and a URL source that has to
        // be retrieved.
        return response.data;
      })
      .then(json => this._loadDeviceLocationBlocks(json.devices, startAt, endAt))
      .catch((error) => {
        console.error(error);
        throw new Error('Failed to retrieve location data - HTTP connection error');
      });
  }

  loadLatestLocations(deviceIds) {
    const url = encodeURI(`${this.baseUrl}/api/v2/devices?device_ids=${deviceIds.join(',')}`);

    return customFetchV2(url, {
      method: 'GET',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load latest locations - HTTP response ${response.status}`);
        }

        return response.data;
      })
      .catch((error) => {
        console.error(error);
        throw new Error('Failed to load latest locations - HTTP connection error');
      });
  }

  _loadDeviceLocationBlocks = (devicesJson, startAt, endAt) => {
    const promises = devicesJson
      .map(device => this._loadDeviceLocationBlock(device, startAt, endAt));

    return Promise.all(promises);
  }

  _loadDeviceLocationBlock = (deviceJson, startAt, endAt) => {
    const segmentPromises = deviceJson.segments
      .map(s => this._loadSegment(s.url));

    return Promise.all(segmentPromises).then((segments) => {
      const segmentPositions = segments.map(s => s.positions);
      const mergedPositions = [].concat(...segmentPositions);

      const positions = mergedPositions.filter((pos) => {
        const posAt = moment(pos.position_at || pos.created_at);
        // Inclusive
        return posAt.isBetween(startAt, endAt, undefined, '[]');
      });

      const segmentEvents = segments.map(s => s.events || []);
      const mergedEvents = [].concat(...segmentEvents);

      const events = mergedEvents.filter((event) => {
        return moment(event.event_at).isBetween(startAt, endAt);
      });

      return new LocationBlock({
        startAt,
        endAt,
        device: {
          id: deviceJson.id,
          imei: deviceJson.imei,
          name: deviceJson.name,
          model: deviceJson.model,
          mapMarkerIcon: deviceJson.mapMarkerIcon,
          audioEnabled: deviceJson.audioEnabled,
          phoneNumber: deviceJson.phoneNumber,
          lastPriorPosition: deviceJson.lastPriorPosition,
        },
        positions,
        events,
      });
    });
  }

  _loadSegment(segmentUrl) {
    // Segment contains start, end, source, url
    const sig = `_loadSegment(${segmentUrl})`;
    this.info(`${sig} - start`);

    return customFetchV2(segmentUrl, {
      method: 'GET',

      /* If this is a cross site request, don't send any applicable cross site cookies. */
      withCredentials: false,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`${sig} - Failed to load segment - HTTP response ${response.status}`);
        }

        return response.data;
      })
      .then(json => json)
      .catch((error) => {
        console.error(error);
        throw new Error('Failed to load segment - HTTP connection error');
      });
  }

  info(msg) {
    // console.log(`LocationDataService: ${msg}`);
  }
}

export default LocationDataService;
